import type { ApiError, ValidationErrors } from '../types';
import { z } from 'zod';

export class ErrorHandler {
  private static errorLog: Array<{ timestamp: string; error: ApiError; context?: any }> = [];
  private static maxLogSize = 50;

  static handleApiError(error: any): ApiError {
    if (error && typeof error === 'object' && 'status' in error && 'message' in error) {
      // Already an ApiError
      return error as ApiError;
    }

    // Handle different error types
    if (error.response) {
      return {
        status: error.response.status,
        message: error.response.data?.detail || error.response.data?.message || 'An error occurred',
        details: error.response.data,
      };
    }

    if (error.request) {
      return {
        status: 0,
        message: 'Network error. Please check your connection.',
        details: error.request,
      };
    }

    return {
      status: 0,
      message: error.message || 'An unexpected error occurred',
      details: error,
    };
  }

  static handleZodError(error: z.ZodError): ValidationErrors {
    const validationErrors: ValidationErrors = {};
    
    error.issues.forEach((err) => {
      const field = err.path.join('.');
      if (!validationErrors[field]) {
        validationErrors[field] = [];
      }
      validationErrors[field].push(err.message);
    });
    
    return validationErrors;
  }

  static handleAuthError(error: ApiError): void {
    if (error.status === 401) {
      // Log the auth error
      this.logError(error, { type: 'authentication' });
      
      // Clear auth token and redirect to login
      localStorage.removeItem('auth_token');
      window.dispatchEvent(new CustomEvent('auth-error', { detail: error }));
    }
  }

  static handleValidationError(error: ApiError): ValidationErrors {
    const validationErrors: ValidationErrors = {};

    if (error.details && error.details.detail) {
      // Handle FastAPI validation errors
      if (Array.isArray(error.details.detail)) {
        error.details.detail.forEach((err: any) => {
          const field = err.loc ? err.loc[err.loc.length - 1] : 'general';
          validationErrors[field] = validationErrors[field] || [];
          validationErrors[field].push(err.msg);
        });
      } else if (typeof error.details.detail === 'object') {
        // Handle object-based validation errors
        Object.entries(error.details.detail).forEach(([field, messages]) => {
          validationErrors[field] = Array.isArray(messages) ? messages : [messages as string];
        });
      } else if (typeof error.details.detail === 'string') {
        // Handle single string validation error
        validationErrors.general = [error.details.detail];
      }
    }

    // Log validation errors for debugging
    this.logError(error, { type: 'validation', fields: Object.keys(validationErrors) });

    return validationErrors;
  }

  static showNotification(error: ApiError, options?: { 
    duration?: number; 
    persistent?: boolean;
    actionLabel?: string;
    onAction?: () => void;
  }): void {
    const { duration = 5000, persistent = false, actionLabel, onAction } = options || {};
    
    // Determine notification type based on error
    let type: 'error' | 'warning' | 'info' = 'error';
    if (this.isNetworkError(error)) {
      type = 'warning';
    } else if (this.isValidationError(error)) {
      type = 'info';
    }

    // Emit a custom event that notification components can listen to
    window.dispatchEvent(new CustomEvent('show-notification', {
      detail: {
        type,
        message: error.message,
        duration: persistent ? 0 : duration,
        actionLabel,
        onAction,
        errorId: error.details?.requestId,
      },
    }));
  }

  static getErrorMessage(error: any): string {
    const apiError = this.handleApiError(error);
    return apiError.message;
  }

  static getUserFriendlyMessage(error: ApiError): string {
    // Return more user-friendly messages based on error type
    if (this.isNetworkError(error)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }
    
    if (this.isAuthError(error)) {
      return 'Your session has expired. Please log in again.';
    }
    
    if (this.isValidationError(error)) {
      return 'Please check your input and try again.';
    }
    
    if (this.isServerError(error)) {
      return 'The server is experiencing issues. Please try again in a few moments.';
    }
    
    return error.message || 'An unexpected error occurred.';
  }

  static isNetworkError(error: ApiError): boolean {
    return error.status === 0 || error.details?.type === 'network_error';
  }

  static isAuthError(error: ApiError): boolean {
    return error.status === 401;
  }

  static isValidationError(error: ApiError): boolean {
    return error.status === 422;
  }

  static isServerError(error: ApiError): boolean {
    return error.status >= 500;
  }

  static isClientError(error: ApiError): boolean {
    return error.status >= 400 && error.status < 500;
  }

  static isRetryableError(error: ApiError): boolean {
    return this.isNetworkError(error) || this.isServerError(error) || error.status === 429;
  }

  static logError(error: ApiError, context?: any): void {
    const logEntry = {
      timestamp: new Date().toISOString(),
      error,
      context,
    };

    // Add to in-memory log
    this.errorLog.unshift(logEntry);
    
    // Keep log size manageable
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Store in localStorage for persistence (development only)
    if (import.meta.env.DEV) {
      try {
        const storedLog = JSON.parse(localStorage.getItem('error_log') || '[]');
        storedLog.unshift(logEntry);
        
        // Keep only last 20 errors in localStorage
        const trimmedLog = storedLog.slice(0, 20);
        localStorage.setItem('error_log', JSON.stringify(trimmedLog));
      } catch (e) {
        console.warn('Failed to store error log:', e);
      }
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.group(`🚨 Error Log - ${error.status || 'Unknown'}`);
      console.error('Error:', error);
      if (context) {
        console.error('Context:', context);
      }
      console.groupEnd();
    }
  }

  static getErrorLog(): Array<{ timestamp: string; error: ApiError; context?: any }> {
    return [...this.errorLog];
  }

  static clearErrorLog(): void {
    this.errorLog = [];
    if (import.meta.env.DEV) {
      localStorage.removeItem('error_log');
    }
  }

  static createRetryHandler(
    originalFunction: () => Promise<any>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ) {
    return async (): Promise<any> => {
      let lastError: ApiError;
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          return await originalFunction();
        } catch (error) {
          lastError = this.handleApiError(error);
          
          // Don't retry if it's not a retryable error
          if (!this.isRetryableError(lastError)) {
            throw lastError;
          }
          
          // Don't retry on the last attempt
          if (attempt === maxRetries) {
            break;
          }
          
          // Calculate delay with exponential backoff
          const delay = baseDelay * Math.pow(2, attempt);
          console.log(`Retrying in ${delay}ms... (attempt ${attempt + 1}/${maxRetries})`);
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
      
      throw lastError!;
    };
  }

  static async withErrorHandling<T>(
    operation: () => Promise<T>,
    options?: {
      showNotification?: boolean;
      retries?: number;
      onError?: (error: ApiError) => void;
    }
  ): Promise<T> {
    const { showNotification = true, retries = 0, onError } = options || {};
    
    try {
      if (retries > 0) {
        const retryHandler = this.createRetryHandler(operation, retries);
        return await retryHandler();
      } else {
        return await operation();
      }
    } catch (error) {
      const apiError = this.handleApiError(error);
      
      // Log the error
      this.logError(apiError);
      
      // Handle specific error types
      if (this.isAuthError(apiError)) {
        this.handleAuthError(apiError);
      }
      
      // Show notification if requested
      if (showNotification) {
        this.showNotification(apiError);
      }
      
      // Call custom error handler if provided
      if (onError) {
        onError(apiError);
      }
      
      throw apiError;
    }
  }
}

export default ErrorHandler;